import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, hasStrokeDraw } from '../skillUsage';

export const calloutWithLeaderLineSkill: CodeoSkill = {
    name: SKILL_NAMES.calloutWithLeaderLine,
    kind: 'codeo',
    description:
        'Point a short label at a specific spot with a leader line and a dot — the explainer ' +
        'callout, for naming a part of a diagram, a figure in a chart, or one element of a layout.',
    documentUsesSkill: check((script, markup) => hasStrokeDraw(script) && /<circle\b/.test(markup)),
    instructions: `# Callout With Leader Line

A dot lands on a point of interest, a line runs out from it, and a short label arrives at the far end. It is the plainest way to say "this bit, here, is called that" — the register of an explainer or a documentary chyron rather than a hand annotation.

The order matters and is the whole craft: dot, then line, then label. Landing the label first and drawing back to the subject reverses the causality and reads as decoration.

## Markup

The elbow is one path so the whole leader draws as a single gesture. Anchor the label to the line's end, not to the frame.

\`\`\`html
<div id="callout-group" style="position: absolute; left: 700px; top: 340px; width: 700px; height: 300px;">
  <svg viewBox="0 0 700 300" style="position: absolute; inset: 0; width: 700px; height: 300px; overflow: visible;">
    <path id="leader" d="M40 250 L40 120 L300 120" fill="none" stroke="var(--l5-color-2, #38bdf8)" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" />
    <circle id="leader-dot" cx="40" cy="250" r="11" fill="var(--l5-color-2, #38bdf8)" />
  </svg>
  <div id="callout-label" style="position: absolute; left: 312px; top: 92px; font-family: var(--l5-font-0), sans-serif; font-size: 40px; font-weight: 700; color: var(--l5-color-0, #101820);">Live captions</div>
</div>
\`\`\`

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const leaderPath = document.querySelector('#leader');
const leaderLength = Math.ceil(leaderPath.getTotalLength()) + 2;
const POINT = 0.9;

gsap.set('#leader-dot', { scale: 0, transformOrigin: 'center center' });
gsap.set(leaderPath, { strokeDasharray: leaderLength, strokeDashoffset: leaderLength + 1 });
gsap.set('#callout-label', { x: -14, opacity: 0 });

tl.to('#leader-dot', { scale: 1, duration: 0.3, ease: 'back.out(2)' }, POINT);
tl.to(leaderPath, { strokeDashoffset: 0, duration: 0.42, ease: 'power2.inOut' }, POINT + 0.14);
tl.to('#callout-label', { x: 0, opacity: 1, duration: 0.36, ease: 'power3.out' }, POINT + 0.46);

window.__timelines = { main: tl };
\`\`\`

The label's small \`x\` offset means it appears to be pushed into place by the line arriving, rather than fading in beside it.

## Adapt

- **Elbow direction**: run the vertical segment first when pointing up at something, horizontal first when pointing across. The line should leave the subject along the axis with the most free space.
- **Straight leader**: a single diagonal segment is cleaner for a short reach; the elbow earns itself when the label must clear other content.
- **Pulsing dot**: a second, larger circle at low opacity scaling out from the dot once, right after it lands, reads as a locator ping. Once only — a repeating ping is a UI element.
- **Multiple callouts**: stagger them 0.35s apart and draw all the dots first, then all the lines. Naming three parts at once wants that grouping or it reads as three separate events.
- **Value callout**: put a figure in the label rather than a name and this becomes a chart annotation — see \`${SKILL_NAMES.statBarsAndFills}\` for what it can point at.
- **Un-draw**: reversing the dash and shrinking the dot clears the callout so the next one can own the frame.

## Pitfalls

- \`getTotalLength()\` must be read from the live path after layout; dash values cannot be hardcoded. Keep the \`+ 2\` on the dash and the \`+ 1\` on the offset, which park the whole path inside the pattern's gap. A dash equal to the measured length puts the path's ends on the dash/gap boundary, where sub-unit slop paints a fraction of a unit that round caps blow up into a full-width dot.
- The dot is \`scale\`-tweened, so set \`transformOrigin: 'center center'\` through \`gsap.set\` — an SVG circle's default origin is the element's box corner, and the dot will swing in from the side without it.
- The SVG needs \`overflow: visible\` or a leader reaching outside its viewBox is clipped.
- Don't let the label overlap what it points at. If there is no clear space, the layout is too full for a callout.
- Three callouts is the ceiling, and three is already a diagram. Past that, label the elements directly.
- The callout must point at something that is already on screen. Drawing a leader to empty space that then fills reads backwards.
- Keep labels to two or three words. A callout carrying a sentence is a caption wearing a leader line.`,
};
